"use client";

import { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Metric = {
  prefix: string;
  value: number;
  suffix: string;
  display: string;
  label: string;
  descriptor: string;
};
const metrics: Metric[] = [{
  prefix: "",
  value: 98,
  suffix: "%",
  display: "98%",
  label: "SUCCESS RATE",
  descriptor: "Proven negotiation outcomes for our clients."
}, {
  prefix: "",
  value: 12,
  suffix: "yrs",
  display: "12yrs",
  label: "MARKET EXPERIENCE",
  descriptor: "Deep expertise in consumer advocacy."
}, {
  prefix: "",
  value: 3500,
  suffix: "+",
  display: "3,500+",
  label: "PURCHASES SAVED",
  descriptor: "Helping families keep more of their money."
}, {
  prefix: "",
  value: 24,
  suffix: "/7",
  display: "24/7",
  label: "ADVOCATE SUPPORT",
  descriptor: "Always here when you need us most."
}];
function CountUp({
  target,
  display,
  start,
  duration = 1200
}: {
  target: number;
  display: string;
  start: boolean;
  duration?: number;
}) {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf: number;
    const startTime = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(target * eased);
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setCurrent(target);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);
  const formatValue = () => {
    if (display.includes("%")) {
      return current.toFixed(0) + "%";
    }
    if (display.includes("yrs")) {
      return Math.round(current) + "yrs";
    }
    if (display.includes(",")) {
      return Math.round(current).toLocaleString() + "+";
    }
    if (display.includes("/")) {
      return Math.round(current) + "/7";
    }
    return Math.round(current).toString();
  };
  return <>{formatValue()}</>;
}
export default function Investment() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.25
  });
  const positions = ["md:col-start-1 md:row-start-1 md:translate-y-0", "md:col-start-2 md:row-start-1 md:translate-y-[60px]", "md:col-start-1 md:row-start-2 md:translate-y-[40px]", "md:col-start-2 md:row-start-2 md:translate-y-[100px]"];
  return <section data-section-id="4575" id="investment-vs-return" ref={ref} className="relative bg-background text-foreground py-40 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="font-default text-xs tracking-[0.3em] uppercase font-bold" style={{
          color: "var(--brand-accent)"
        }}>
            — 03 / IMPACT
          </span>
        </div>

        {}
        <div className="grid grid-cols-12 mb-24 md:mb-32">
          <h2 className={`col-span-12 md:col-span-8 font-default font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tighter text-primary transition-all duration-1000 delay-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            SAVINGS THAT
            <br />
            <em className="not-italic font-bold" style={{
            color: "var(--brand-accent)"
          }}>
              TRULY MATTER.
            </em>
          </h2>
        </div>

        {}
        <div className="relative">
          {}
          <svg className="hidden md:block absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100" style={{
          color: "var(--brand-mid)"
        }} aria-hidden="true">
            <line x1="20" y1="15" x2="78" y2="28" stroke="currentColor" strokeWidth="0.08" strokeDasharray="0.4 0.6" opacity={isVisible ? "0.6" : "0"} style={{
            transition: "opacity 1.2s ease 0.4s"
          }} />
            <line x1="22" y1="20" x2="25" y2="58" stroke="currentColor" strokeWidth="0.08" strokeDasharray="0.4 0.6" opacity={isVisible ? "0.6" : "0"} style={{
            transition: "opacity 1.2s ease 0.6s"
          }} />
            <line x1="30" y1="62" x2="80" y2="88" stroke="currentColor" strokeWidth="0.08" strokeDasharray="0.4 0.6" opacity={isVisible ? "0.6" : "0"} style={{
            transition: "opacity 1.2s ease 0.8s"
          }} />
            <line x1="80" y1="32" x2="83" y2="84" stroke="currentColor" strokeWidth="0.08" strokeDasharray="0.4 0.6" opacity={isVisible ? "0.6" : "0"} style={{
            transition: "opacity 1.2s ease 1s"
          }} />
          </svg>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-20 md:gap-y-24 md:gap-x-12 lg:gap-x-20 relative">
            {metrics.map((metric, i) => <div data-index={i} key={i} className={`${positions[i]} transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${300 + i * 150}ms`
          }}>
                <div className={`flex flex-col ${i % 2 === 1 ? "md:items-end md:text-right" : "items-start"}`}>
                  {}
                  <div className="font-default font-bold leading-none tracking-tighter" style={{
                color: "var(--brand-deep)",
                fontSize: "clamp(5rem, 12vw, 9rem)"
              }}>
                    <CountUp target={metric.value} display={metric.display} start={isVisible} />
                  </div>

                  {}
                  <div className="mt-6 mb-5 h-px w-16" style={{
                backgroundColor: "var(--brand-accent)"
              }} aria-hidden="true" />

                  {}
                  <div className="font-default text-[11px] tracking-[0.28em] uppercase mb-3 font-bold" style={{
                color: "var(--brand-deep)"
              }}>
                    {metric.label}
                  </div>

                  {}
                  <p className="font-default font-normal text-base md:text-lg leading-relaxed max-w-xs" style={{
                color: "var(--text-muted)"
              }}>
                    {metric.descriptor}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}